<template>
    <div>
        <input v-if="searchable" ref="searchbox" type="text" placeholder="Search..." class="map-searchbox" />
        <div ref="map" class="uk-height-1-1"></div>
    </div>
</template>

<style>
    .map-searchbox {
        padding: 0.4rem;
        margin-top: 0.8rem;
        width: 25rem;
    }
</style>

<script type="text/javascript">
import { mapGetters } from "vuex"
import { isString, forOwn } from "lodash"

import utilities from "../mixins/Is"
import uses_color_palette from "../mixins/UsesColorPalette"

const coordinate = (latitude_or_longitude) => {
    if(isString(latitude_or_longitude)) return parseFloat(latitude_or_longitude)
    return latitude_or_longitude
}

export default {
    watch: {
        becameVisible: "refresh_map",
        locations () {
            this.mark_locations()
        }
    },
    mounted () {
        this.init_map()
    },
    mixins: [utilities, uses_color_palette],
    methods: {
        refresh_map () {
            if(this.map) {
                setTimeout(() => {
                    window.google.maps.event.trigger(this.map, "resize")
                    this.mark_locations()
                }, 500)
            }
        },
        map_icon (fillColor, text, textColor, outlineColor) {
            fillColor = fillColor.replace(/#/, "")
            text = text || " "
            textColor = textColor || "00274F"
            outlineColor = outlineColor || "00274F"
            return "https://chart.googleapis.com/chart?cht=d&chdp=mapsapi&chl=pin%27i\\%27[" + text + "%27-2%27f\\hv%27a\\]h\\]o\\" + fillColor + "%27fC\\" + textColor + "%27tC\\" + outlineColor + "%27eC\\Lauto%27f\\&ext=.png"
        },
        info_bubble_content (data) {
            let content = `<h3 class="uk-margin-bottom-remove"><a href="/project/${data.id}">${data.number} ${data.name}</a></h3>`

            if(data.location) content += `<div class="uk-grid uk-grid-small uk-text-muted"><div class="uk-width-1-1">${data.location}</div></div>`

            let members = []
            const add_member = (user_id, suffix = "") => {
                const user = this.entity_record("user", user_id)
                if(user) members.push(`${user.initials}${suffix}`)
            }
            if(data.team && data.team.pic) add_member(data.team.pic, " (PIC)")
            if(data.team && data.team.pm) add_member(data.team.pm, " (PM)")
            if(data.team) {
                data.team.designers.forEach(designer_id => {
                    add_member(designer_id)
                })
            }
            /*
            if(data.team && data.team.pic && data.team.user[data.team.pic]) members.push(data.team.user[data.team.pic].initials + " (PIC)")
            if(data.team && data.team.pm && data.team.user[data.team.pm]) members.push(data.team.user[data.team.pm].initials + " (PM)")
            if(data.team) data.team.designers.forEach((designer_id) => {
                if(designer_id && data.team.user[designer_id]) members.push(data.team.user[designer_id].initials)
            })
            */
            members = this.nibnut_filter("nibnut.concat", [members, ", "])
            if(members) content += `<div class="uk-grid uk-grid-small"><div class="uk-width-3-10"><strong class="uk-text-uppercase">Team</strong></div><div class="uk-width-7-10">${members}</div></div>`

            if(data.architect) content += `<div class="uk-grid uk-grid-small"><div class="uk-width-3-10"><strong class="uk-text-uppercase">Architect</strong></div><div class="uk-width-7-10">${data.architect}</div></div>`

            if(data.type) content += `<div class="uk-grid uk-grid-small"><div class="uk-width-3-10"><strong class="uk-text-uppercase">Project Type</strong></div><div class="uk-width-7-10">${data.type}</div></div>`

            if(data.completed || data.current_phase) {
                content += "<div class=\"uk-grid uk-grid-small\"><div class=\"uk-width-1-1\"><strong class=\"uk-text-uppercase\">"

                if(data.completed) {
                    const when = this.nibnut_filter("nibnut.date", [data.completed, "YYYY"])
                    content += `Completed ${when}`
                } else if(data.current_phase) {
                    content += `${data.current_phase}`
                }

                content += "</strong></div></div>"
            }

            return content
        },
        mark_locations (center) {
            if(!this.map || this.locations === null) return

            const active_marker_ids = []
            this.locations.forEach((location, index) => {
                const coordinates = { lat: coordinate(location.latitude), lng: coordinate(location.longitude) }
                if(center && !index) this.map.setCenter(coordinates)

                const marker_id = location.number ? location.number : coordinates.lat + "x" + coordinates.lng
                if(!this.markers[marker_id]) {
                    this.markers[marker_id] = new window.google.maps.Marker({
                        position: coordinates,
                        animation: null, // window.google.maps.Animation.DROP,
                        icon: this.map_icon(location.completed ? this.color_completed.background : this.color_active.background),
                        title: location.name || ""
                    })
                    this.markers[marker_id]._lam_data = location
                    this.markers[marker_id].addListener("click", () => {
                        const marker = this.markers[marker_id]
                        if(!!marker._lam_data && !!marker._lam_data.number) this.popup.setContent(this.info_bubble_content(marker._lam_data))
                        this.popup.open(this.map, marker)
                    })
                } else this.markers[marker_id].setPosition(coordinates)

                active_marker_ids.push(marker_id)
            })

            forOwn(this.markers, (marker, marker_id) => {
                if(marker_id.match(/^search_result_/)) return
                if(active_marker_ids.indexOf(marker_id) >= 0) marker.setMap(this.map)
                else marker.setMap(null)
            })
        },
        init_map () {
            if(!this.map && this.$refs.map) {
                this.map = new window.google.maps.Map(this.$refs.map, {
                    mapTypeControl: true,
                    center: { lat: 42.3881203, lng: -71.13206209999998 },
                    zoom: this.initialZoom,
                    styles: [
                        {
                            featureType: "administrative",
                            elementType: "all",
                            stylers: [
                                {
                                    saturation: "-100"
                                }
                            ]
                        },
                        {
                            featureType: "administrative.province",
                            elementType: "all",
                            stylers: [
                                {
                                    visibility: this.boundaries ? "on" : "off"
                                }
                            ]
                        },
                        {
                            featureType: "landscape",
                            elementType: "all",
                            stylers: [
                                {
                                    saturation: -100
                                },
                                {
                                    lightness: 65
                                },
                                {
                                    visibility: "on"
                                }
                            ]
                        },
                        {
                            featureType: "poi",
                            elementType: "all",
                            stylers: [
                                {
                                    saturation: -100
                                },
                                {
                                    lightness: "50"
                                },
                                {
                                    visibility: "simplified"
                                }
                            ]
                        },
                        {
                            featureType: "road",
                            elementType: "all",
                            stylers: [
                                {
                                    saturation: "-100"
                                }
                            ]
                        },
                        {
                            featureType: "road.highway",
                            elementType: "all",
                            stylers: [
                                {
                                    visibility: "simplified"
                                }
                            ]
                        },
                        {
                            featureType: "road.arterial",
                            elementType: "all",
                            stylers: [
                                {
                                    lightness: "30"
                                }
                            ]
                        },
                        {
                            featureType: "road.local",
                            elementType: "all",
                            stylers: [
                                {
                                    lightness: "40"
                                }
                            ]
                        },
                        {
                            featureType: "transit",
                            elementType: "all",
                            stylers: [
                                {
                                    saturation: -100
                                },
                                {
                                    visibility: "simplified"
                                }
                            ]
                        },
                        {
                            featureType: "water",
                            elementType: "geometry",
                            stylers: [
                                {
                                    hue: "#ffff00"
                                },
                                {
                                    lightness: -25
                                },
                                {
                                    saturation: -97
                                }
                            ]
                        },
                        {
                            featureType: "water",
                            elementType: "labels",
                            stylers: [
                                {
                                    lightness: -25
                                },
                                {
                                    saturation: -100
                                }
                            ]
                        }
                    ]
                })

                this.popup = new window.google.maps.InfoWindow({
                    content: ""
                })

                if(this.searchable && this.$refs.searchbox) {
                    this.searchbox = new window.google.maps.places.SearchBox(this.$refs.searchbox)
                    setTimeout(() => {
                        this.map.controls[window.google.maps.ControlPosition.TOP_LEFT].push(this.$refs.searchbox)
                    }, 50)

                    this.map.addListener("bounds_changed", () => {
                        this.searchbox.setBounds(this.map.getBounds())
                    })

                    this.searchbox.addListener("places_changed", () => {
                        const places = this.searchbox.getPlaces()

                        if(!places.length) return

                        forOwn(this.markers, (marker, marker_id) => {
                            if(marker_id.match(/^search_result_/)) marker.setMap(null)
                        })

                        const bounds = new window.google.maps.LatLngBounds()
                        places.forEach((place, index) => {
                            if(!place.geometry) return

                            const marker_id = "search_result_" + index

                            this.markers[marker_id] = new window.google.maps.Marker({
                                map: this.map,
                                icon: this.map_icon(this.color_pending.background),
                                title: place.name,
                                position: place.geometry.location,
                                zIndex: 1000
                            })

                            if(place.geometry.viewport) bounds.union(place.geometry.viewport)
                            else bounds.extend(place.geometry.location)
                        })

                        this.map.fitBounds(bounds)
                    })
                }

                this.mark_locations(true)
            }
        }
    },
    computed: {
        ...mapGetters(["entity_record"])
    },
    props: {
        locations: {
            type: Array,
            default () {
                return [] // [{latitude: ..., longitude: ...(, title: ...)(, description: ...)}]
            }
        },
        becameVisible: {
            type: Boolean,
            default: false
        },
        initialZoom: {
            type: Number,
            default: 15
        },
        boundaries: {
            type: Boolean,
            default: false
        },
        searchable: {
            type: Boolean,
            default: false
        }
    },
    data () {
        return {
            map: null,
            popup: null,
            searchbox: null,
            markers: {}
        }
    }
}
</script>
